import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";

export type PfaBillingProfileRow = Tables<"pfa_billing_profiles">;

export type BillingAddressSnapshot = {
  profileId: string | null;
  label: string;
  companyName: string | null;
  houseNumber: string;
  streetName: string;
  city: string | null;
  postalCode: string;
  country: string;
};

export type BillingProfileDraft = {
  label: string;
  companyName: string;
  houseNumber: string;
  streetName: string;
  city: string;
  postalCode: string;
  country: string;
  isDefault: boolean;
};

export const EMPTY_BILLING_PROFILE_DRAFT: BillingProfileDraft = {
  label: "",
  companyName: "",
  houseNumber: "",
  streetName: "",
  city: "",
  postalCode: "",
  country: "",
  isDefault: false,
};

export function billingProfileRowToSnapshot(row: PfaBillingProfileRow): BillingAddressSnapshot {
  return {
    profileId: row.id,
    label: row.label,
    companyName: row.company_name,
    houseNumber: row.house_number,
    streetName: row.street_name,
    city: row.city,
    postalCode: row.postal_code,
    country: row.country,
  };
}

export function billingDraftToSnapshot(
  draft: BillingProfileDraft,
  profileId: string | null = null,
  options?: { trimFields?: boolean },
): BillingAddressSnapshot {
  const trimFields = options?.trimFields !== false;
  const trim = (value: string) => (trimFields ? value.trim() : value);
  return {
    profileId,
    label: trim(draft.label),
    companyName: trim(draft.companyName) || null,
    houseNumber: trim(draft.houseNumber),
    streetName: trim(draft.streetName),
    city: trim(draft.city) || null,
    postalCode: trim(draft.postalCode),
    country: trim(draft.country),
  };
}

/** Live editing snapshot — preserves trailing spaces while typing (e.g. street name). */
export function billingDraftToLiveSnapshot(
  draft: BillingProfileDraft,
  profileId: string | null = null,
): BillingAddressSnapshot {
  return billingDraftToSnapshot(draft, profileId, { trimFields: false });
}

export function billingSnapshotToDraft(snapshot: BillingAddressSnapshot): BillingProfileDraft {
  return {
    label: snapshot.label,
    companyName: snapshot.companyName ?? "",
    houseNumber: snapshot.houseNumber,
    streetName: snapshot.streetName,
    city: snapshot.city ?? "",
    postalCode: snapshot.postalCode,
    country: snapshot.country,
    isDefault: false,
  };
}

export function billingProfileRowToDraft(row: PfaBillingProfileRow): BillingProfileDraft {
  return {
    label: row.label,
    companyName: row.company_name ?? "",
    houseNumber: row.house_number,
    streetName: row.street_name,
    city: row.city ?? "",
    postalCode: row.postal_code,
    country: row.country,
    isDefault: row.is_default,
  };
}

export function normalizeBillingAddressSnapshot(raw: unknown): BillingAddressSnapshot | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const houseNumber = typeof o.houseNumber === "string" ? o.houseNumber.trim() : "";
  const streetName = typeof o.streetName === "string" ? o.streetName.trim() : "";
  const postalCode = typeof o.postalCode === "string" ? o.postalCode.trim() : "";
  const country = typeof o.country === "string" ? o.country.trim() : "";
  const label = typeof o.label === "string" ? o.label.trim() : "";
  if (!houseNumber || !streetName || !postalCode || !country || !label) return null;
  return {
    profileId: typeof o.profileId === "string" ? o.profileId : null,
    label,
    companyName: typeof o.companyName === "string" ? o.companyName.trim() || null : null,
    houseNumber,
    streetName,
    city: typeof o.city === "string" ? o.city.trim() || null : null,
    postalCode,
    country,
  };
}

export function isBillingAddressComplete(snapshot: BillingAddressSnapshot | null | undefined): boolean {
  return normalizeBillingAddressSnapshot(snapshot) != null;
}

export function formatBillingAddressLabel(
  snapshot: BillingAddressSnapshot | null | undefined,
): string | null {
  const normalized = normalizeBillingAddressSnapshot(snapshot);
  if (!normalized) return null;
  const streetLine = [normalized.houseNumber, normalized.streetName].filter(Boolean).join(" ").trim();
  const locality = [normalized.city, normalized.postalCode].filter(Boolean).join(" ").trim();
  const parts = [normalized.companyName, streetLine, locality, normalized.country].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
}

export function formatBillingAddressShortLabel(
  snapshot: BillingAddressSnapshot | null | undefined,
): string | null {
  const normalized = normalizeBillingAddressSnapshot(snapshot);
  if (!normalized) return null;
  const streetLine = [normalized.houseNumber, normalized.streetName].filter(Boolean).join(" ").trim();
  return [normalized.label, streetLine].filter(Boolean).join(" — ") || normalized.label;
}

export async function fetchPfaBillingProfiles(userId: string): Promise<PfaBillingProfileRow[]> {
  const { data, error } = await supabase
    .from("pfa_billing_profiles")
    .select("*")
    .eq("user_id", userId)
    .order("is_default", { ascending: false })
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function createPfaBillingProfile(
  userId: string,
  draft: BillingProfileDraft,
): Promise<PfaBillingProfileRow> {
  const payload: TablesInsert<"pfa_billing_profiles"> = {
    user_id: userId,
    label: draft.label.trim(),
    company_name: draft.companyName.trim() || null,
    house_number: draft.houseNumber.trim(),
    street_name: draft.streetName.trim(),
    city: draft.city.trim() || null,
    postal_code: draft.postalCode.trim(),
    country: draft.country.trim(),
    is_default: draft.isDefault,
  };
  const { data, error } = await supabase.from("pfa_billing_profiles").insert(payload).select("*").single();
  if (error) throw error;
  return data;
}

export async function updatePfaBillingProfile(
  profileId: string,
  draft: BillingProfileDraft,
): Promise<PfaBillingProfileRow> {
  const payload: TablesUpdate<"pfa_billing_profiles"> = {
    label: draft.label.trim(),
    company_name: draft.companyName.trim() || null,
    house_number: draft.houseNumber.trim(),
    street_name: draft.streetName.trim(),
    city: draft.city.trim() || null,
    postal_code: draft.postalCode.trim(),
    country: draft.country.trim(),
    is_default: draft.isDefault,
  };
  const { data, error } = await supabase
    .from("pfa_billing_profiles")
    .update(payload)
    .eq("id", profileId)
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

export async function deletePfaBillingProfile(profileId: string): Promise<void> {
  const { error } = await supabase.from("pfa_billing_profiles").delete().eq("id", profileId);
  if (error) throw error;
}

export async function setDefaultPfaBillingProfile(profileId: string): Promise<void> {
  const { error } = await supabase
    .from("pfa_billing_profiles")
    .update({ is_default: true })
    .eq("id", profileId);
  if (error) throw error;
}
